import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  RefreshCw, ShieldAlert, Search, Filter, ArrowLeft, ChevronDown, ChevronUp, Loader2, Download, Activity,
  UserPlus, UserMinus, KeyRound, Building2, Trash2, LogIn, Mail, Pencil, Database, Upload as UploadIcon, FileDown,
} from "lucide-react";

// Action -> presentation metadata. Keeps the table scannable.
const ACTION_META = {
  user_created:               { icon: UserPlus,   color: "#0F766E", bg: "rgba(20,184,166,0.10)", label: "User created" },
  user_updated:               { icon: Pencil,     color: "#0F766E", bg: "rgba(20,184,166,0.10)", label: "User updated" },
  user_activated:             { icon: UserPlus,   color: "#0F766E", bg: "rgba(20,184,166,0.10)", label: "User activated" },
  user_deactivated:           { icon: UserMinus,  color: "#B45309", bg: "rgba(245,158,11,0.12)", label: "User deactivated" },
  user_deleted:               { icon: Trash2,     color: "#B91C1C", bg: "rgba(239,68,68,0.10)",  label: "User deleted" },
  user_password_reset_by_admin:{ icon: KeyRound,  color: "#B45309", bg: "rgba(245,158,11,0.12)", label: "Password reset (admin)" },
  user_invite_resent:         { icon: Mail,       color: "#0F766E", bg: "rgba(20,184,166,0.10)", label: "Invite re-sent" },
  organization_created:       { icon: Building2,  color: "#0F766E", bg: "rgba(20,184,166,0.10)", label: "Organization created" },
  organization_deleted:       { icon: Trash2,     color: "#B91C1C", bg: "rgba(239,68,68,0.10)",  label: "Organization deleted" },
  organization_plan_changed:  { icon: Pencil,     color: "#7C3AED", bg: "rgba(124,58,237,0.10)", label: "Plan changed" },
  phi_upload_rejected:        { icon: ShieldAlert,color: "#B91C1C", bg: "rgba(239,68,68,0.14)",  label: "PHI upload rejected" },
  login:                      { icon: LogIn,      color: "#475569", bg: "rgba(100,116,139,0.10)", label: "Login" },
  login_success:              { icon: LogIn,      color: "#0F766E", bg: "rgba(20,184,166,0.10)", label: "Login success" },
  login_failed:               { icon: ShieldAlert,color: "#B91C1C", bg: "rgba(239,68,68,0.10)",  label: "Login failed" },
  first_login_password_set:   { icon: KeyRound,   color: "#0F766E", bg: "rgba(20,184,166,0.10)", label: "First-login password set" },
  password_changed:           { icon: KeyRound,   color: "#0F766E", bg: "rgba(20,184,166,0.10)", label: "Password changed" },
  password_reset_requested:   { icon: KeyRound,   color: "#475569", bg: "rgba(100,116,139,0.10)", label: "Password reset requested" },
  password_reset_completed:   { icon: KeyRound,   color: "#0F766E", bg: "rgba(20,184,166,0.10)", label: "Password reset completed" },
  dataset_uploaded:           { icon: UploadIcon, color: "#0F766E", bg: "rgba(20,184,166,0.10)", label: "Dataset uploaded" },
  dataset_deleted:            { icon: Trash2,     color: "#B91C1C", bg: "rgba(239,68,68,0.10)",  label: "Dataset deleted" },
  export_generated:           { icon: FileDown,   color: "#475569", bg: "rgba(100,116,139,0.10)", label: "Export generated" },
};

const DEFAULT_META = { icon: Activity, color: "#475569", bg: "rgba(100,116,139,0.10)", label: null };

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

function actionLabel(action) {
  const meta = ACTION_META[action] || DEFAULT_META;
  return meta.label || action.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function ActionPill({ action }) {
  const meta = ACTION_META[action] || DEFAULT_META;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider"
      style={{ color: meta.color, background: meta.bg }}
      data-testid={`audit-action-${action}`}
    >
      <Icon className="h-3 w-3" />
      {actionLabel(action)}
    </span>
  );
}

function ExpandedDetails({ event }) {
  const details = event.details || {};
  const hasDetails = details && Object.keys(details).length > 0;
  return (
    <div className="bg-slate-50 px-4 py-3 border-t border-slate-200" data-testid="audit-row-expanded">
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[11px]">
        <div><span className="text-slate-500">Event ID:</span> <code className="text-slate-900 font-mono">{event.id}</code></div>
        <div><span className="text-slate-500">Timestamp:</span> <span className="text-slate-900">{fmtTime(event.ts)}</span></div>
        {event.actor_email && <div><span className="text-slate-500">Actor:</span> <span className="text-slate-900">{event.actor_email}</span></div>}
        {event.target_email && <div><span className="text-slate-500">Target:</span> <span className="text-slate-900">{event.target_email}</span></div>}
        {event.ip && <div><span className="text-slate-500">IP:</span> <code className="text-slate-900 font-mono">{event.ip}</code></div>}
      </div>
      {hasDetails && (
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Details</div>
          <pre className="text-[11px] font-mono text-slate-800 bg-white border border-slate-200 rounded p-2 overflow-x-auto max-h-48">
{JSON.stringify(details, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const headers = ["timestamp", "action", "actor_email", "target_email", "ip", "details"];
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = typeof v === "string" ? v : JSON.stringify(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push([r.ts, r.action, r.actor_email || "", r.target_email || "", r.ip || "", JSON.stringify(r.details || {})].map(escape).join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function AuditLogPage() {
  const { user: me } = useAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [limit, setLimit] = useState(100);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = async (nextLimit = limit) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/audit-log?limit=${nextLimit}`);
      setEvents(data.events || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load audit log.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const actionOptions = useMemo(() => {
    const set = new Set(events.map((e) => e.action).filter(Boolean));
    return Array.from(set).sort();
  }, [events]);

  const actorOptions = useMemo(() => {
    const set = new Set(events.map((e) => e.actor_email).filter(Boolean));
    return Array.from(set).sort();
  }, [events]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((e) => {
      if (actionFilter && e.action !== actionFilter) return false;
      if (actorFilter && e.actor_email !== actorFilter) return false;
      if (!q) return true;
      const hay = [
        e.action, e.actor_email, e.target_email, e.ip,
        actionLabel(e.action), JSON.stringify(e.details || {}),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [events, search, actionFilter, actorFilter]);

  const clearFilters = () => { setSearch(""); setActionFilter(""); setActorFilter(""); };
  const hasFilters = search || actionFilter || actorFilter;

  if (me && me.role !== "admin") {
    return (
      <AppShell>
        <div className="surface-card p-6 max-w-lg mx-auto text-center">
          <ShieldAlert className="h-10 w-10 mx-auto" style={{ color: "#EF4444" }} />
          <h2 className="font-display text-xl text-slate-900 mt-3">Admin access required</h2>
          <p className="text-sm text-slate-600 mt-2">You don't have permission to view the audit log.</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="fade-up" data-testid="admin-audit-log-page">
        <Link to="/admin/users" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-teal-700 mb-3" data-testid="audit-back-link">
          <ArrowLeft className="h-3 w-3" /> Back to User Access Management
        </Link>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <span className="label-overline">Admin</span>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-1.5 tracking-tight">
              Audit Log
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Every security-relevant action — user provisioning, role changes, logins, password resets, data uploads — captured with actor, target, and timestamp.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => load(limit)} disabled={loading} className="rounded-lg" data-testid="audit-refresh">
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadCsv(`audit-log-${new Date().toISOString().slice(0, 10)}.csv`, filtered)} disabled={!filtered.length} className="rounded-lg" data-testid="audit-export-csv">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="surface-card p-3 mb-4" data-testid="audit-filters">
          <div className="grid sm:grid-cols-4 gap-2">
            <div className="sm:col-span-2 relative">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search action, actor, target, IP, details…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-9 rounded-lg text-sm"
                data-testid="audit-search"
              />
            </div>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="h-9 rounded-lg border border-slate-300 px-3 text-sm bg-white"
              data-testid="audit-action-filter"
            >
              <option value="">All actions</option>
              {actionOptions.map((a) => <option key={a} value={a}>{actionLabel(a)}</option>)}
            </select>
            <select
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
              className="h-9 rounded-lg border border-slate-300 px-3 text-sm bg-white"
              data-testid="audit-actor-filter"
            >
              <option value="">All actors</option>
              {actorOptions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-between mt-2.5">
            <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
              <Filter className="h-3 w-3" />
              Showing <span className="font-semibold text-slate-900 tabular-nums">{filtered.length}</span> of <span className="font-semibold text-slate-900 tabular-nums">{events.length}</span> events
              {hasFilters && (
                <button onClick={clearFilters} className="ml-2 text-teal-700 hover:underline" data-testid="audit-clear-filters">Clear filters</button>
              )}
            </div>
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              Load
              {[100, 250, 500].map((n) => (
                <button
                  key={n}
                  onClick={() => { setLimit(n); load(n); }}
                  className={`px-2 py-0.5 rounded font-semibold ${limit === n ? "bg-teal-600 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                  data-testid={`audit-limit-${n}`}
                >{n}</button>
              ))}
              events
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="surface-card overflow-hidden" data-testid="audit-table-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-3 w-[170px]">When</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Actor</th>
                  <th className="px-4 py-3">Target</th>
                  <th className="px-4 py-3 w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center py-10 text-slate-400"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Loading audit events…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-10 text-slate-400">
                    <Database className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                    {events.length === 0 ? "No audit events yet." : "No events match your filters."}
                  </td></tr>
                ) : filtered.map((e) => {
                  const isOpen = !!expanded[e.id];
                  return (
                    <React.Fragment key={e.id}>
                      <tr
                        className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 cursor-pointer"
                        onClick={() => setExpanded((s) => ({ ...s, [e.id]: !s[e.id] }))}
                        data-testid={`audit-row-${e.id}`}
                      >
                        <td className="px-4 py-3 align-top">
                          <div className="text-xs font-medium text-slate-900 tabular-nums">{fmtTime(e.ts)}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5">{relativeTime(e.ts)}</div>
                        </td>
                        <td className="px-4 py-3 align-top"><ActionPill action={e.action} /></td>
                        <td className="px-4 py-3 align-top text-xs text-slate-700">
                          {e.actor_email ? (
                            <span className="font-medium text-slate-900">{e.actor_email}</span>
                          ) : <span className="text-slate-400 italic">system</span>}
                          {e.ip && <div className="text-[10px] text-slate-400 font-mono mt-0.5">{e.ip}</div>}
                        </td>
                        <td className="px-4 py-3 align-top text-xs text-slate-700">
                          {e.target_email ? (
                            <span className="font-medium text-slate-900">{e.target_email}</span>
                          ) : e.details?.organization_id ? (
                            <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">org {String(e.details.organization_id).slice(0, 8)}</Badge>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 align-top text-right">
                          {isOpen ? <ChevronUp className="h-3.5 w-3.5 text-slate-400 inline" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400 inline" />}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/40">
                          <td colSpan={5} className="p-0"><ExpandedDetails event={e} /></td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-[11px] text-slate-400 mt-3" data-testid="audit-footer">
          Audit log is append-only and tamper-evident. Events are retained indefinitely and exportable for compliance review.
        </p>
      </div>
    </AppShell>
  );
}
